import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import TodoRegisterLayer from '../../../../components/todo/TodoRegisterLayer';

function ChecklistTab({
  checklists = [],
  newChecklist,
  setNewChecklist,
  handleAddChecklist,
  handleToggleChecklist,
  handleEditChecklist,
  handleDeleteChecklist,
  isFullScreenMode,
  currentUser,
  isProjectMaster,
  isAssignee,
  isPureWatcher,
  project,
  task
}) {
  const [viewMode, setViewMode] = useState('MINE'); // 'ALL', 'MINE', 'GROUP'
  const [hideCompleted, setHideCompleted] = useState(false);
  const location = useLocation();

  // --- [추가] 외부(To-Do 원본보기 등)에서 넘어왔을 때 해당 항목으로 스크롤 및 하이라이트 ---
  useEffect(() => {
    if (checklists && checklists.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const focusId = searchParams.get('focusId');
      const tab = searchParams.get('tab');
      
      if (focusId && tab === 'checklist') {
        // 내 필터에 걸려 안 보일 수 있으므로 전체보기로 전환
        setViewMode('ALL');
        
        let attempts = 0;
        const tryScroll = setInterval(() => {
          attempts++;
          const el = document.getElementById(`checklist-${focusId}`);
          if (el) {
            clearInterval(tryScroll);
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            const originalBg = el.style.backgroundColor;
            const originalTransition = el.style.transition;
            el.style.transition = 'background-color 0.5s ease';
            el.style.backgroundColor = '#fff3cd'; // warning light
            
            setTimeout(() => {
              el.style.backgroundColor = originalBg || '';
              setTimeout(() => {
                el.style.transition = originalTransition || '';
              }, 500);
            }, 2000);
          } else if (attempts > 10) {
            clearInterval(tryScroll); // 최대 1초(100ms * 10) 대기 후 포기
            alert('이미 삭제되었거나 접근 권한이 없는 항목입니다.');
          }
        }, 100);
        return () => clearInterval(tryScroll);
      }
    }
  }, [checklists, location.search]);

  // 텍스트 인라인 에디팅
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  // 빠른 추가 (Textarea)
  const quickAddRef = useRef(null);
  const editAreaRef = useRef(null);

  // 작성자별 아코디언
  const [expandedGroups, setExpandedGroups] = useState({});

  // To-Do 등록 팝업 상태
  const [todoAnchorEl, setTodoAnchorEl] = useState(null);
  const [todoSource, setTodoSource] = useState(null);

  const handleOpenTodoRegister = (e, item) => {
    e.stopPropagation();
    setTodoAnchorEl({ current: e.currentTarget });
    setTodoSource({
      ref_type: 'pms_checklist',
      ref_table: 'tdx_pms_task_checklist',
      ref_id: item.id,
      add_title: project?.title || '',
      add_subtype: 'task',
      add_id1: project?.id,
      add_id2: task?.id,
      title: task?.task_name || task?.title || '태스크',
      memo: item.content || '',
    });
  };

  // 더보기(펼치기) 상태 관리 및 판별 함수
  const [expandedItems, setExpandedItems] = useState({});
  const isContentLong = (content) => {
    if (!content) return false;
    return content.split('\n').length > 3 || content.length > 100;
  };

  // 탭 변경 시 화면 UI 초기화 로직
  const handleTabChange = (mode) => {
    if (viewMode === mode) return; // 동일 탭 클릭 시 무시
    setViewMode(mode);
    setEditingId(null);
    setEditContent('');
    setExpandedItems({});
    setHideCompleted(false);
    if (setNewChecklist) setNewChecklist('');
  };

  useEffect(() => {
    if (viewMode === 'GROUP') {
      const gData = checklists.reduce((acc, curr) => {
        if (curr.user && !acc[curr.user.id]) acc[curr.user.id] = { user: curr.user };
        return acc;
      }, {});

      const sortedGroups = Object.values(gData).sort((a, b) => {
        if (a.user.id === currentUser?.id) return -1;
        if (b.user.id === currentUser?.id) return 1;
        return (a.user.name || '').localeCompare(b.user.name || '');
      });

      const groups = {};
      if (sortedGroups.length > 0) {
        groups[sortedGroups[0].user.id] = true;
      }
      setExpandedGroups(groups);
    }
  }, [viewMode, checklists, currentUser]);

  // Textarea 자동 높이 조절 로직
  const handleTextareaResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // 날짜 포맷 (YYYY-MM-DD HH:mm:ss)
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = n => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // --- [액션 핸들러] ---
  const handleToggle = (id, currentStatus) => {
    if (isPureWatcher) return; // 참조자는 클릭 무시
    if (handleToggleChecklist) handleToggleChecklist(id, currentStatus);
  };

  const handleDelete = (id) => {
    if (handleDeleteChecklist) handleDeleteChecklist(id);
  };

  const handleQuickAddSave = (e) => {
    if (handleAddChecklist) {
      handleAddChecklist(e);
      if (quickAddRef.current) {
        quickAddRef.current.style.height = 'auto';
        quickAddRef.current.focus();
      }
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditContent(item.content);
    setTimeout(() => {
      if (editAreaRef.current) {
        editAreaRef.current.style.height = 'auto';
        editAreaRef.current.style.height = editAreaRef.current.scrollHeight + 'px';
        editAreaRef.current.focus();
      }
    }, 10);
  };

  const saveEdit = () => {
    if (editContent.trim() !== '') {
      if (handleEditChecklist) handleEditChecklist(editingId, editContent.trim());
    }
    setEditingId(null);
  };

  const toggleGroup = (userId) => {
    setExpandedGroups(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // --- [데이터 집계 및 정렬] ---
  const baseItems = viewMode === 'MINE' ? checklists.filter(c => c.user && c.user.id === currentUser?.id) : checklists;

  const getFilteredData = () => {
    // 1. 필터링 복사본 생성
    let filtered = [...baseItems];
    if (hideCompleted) filtered = filtered.filter(c => !c.is_completed);

    // 2. 정렬 로직 (완료는 밑으로, 등록일 내림차순)
    filtered.sort((a, b) => {
      if (a.is_completed !== b.is_completed) {
        return a.is_completed ? 1 : -1; // 완료가 true면 뒤로(1)
      }
      // 상태가 같으면 최신 등록일 우선
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return filtered;
  };

  const filteredData = getFilteredData();
  const overallProgress = baseItems.length === 0 ? 0 : Math.round((baseItems.filter(c => c.is_completed).length / baseItems.length) * 100);

  const groupedData = filteredData.reduce((acc, curr) => {
    if (curr.user) {
      if (!acc[curr.user.id]) acc[curr.user.id] = { user: curr.user, items: [] };
      acc[curr.user.id].items.push(curr);
    }
    return acc;
  }, {});

  // --- [렌더링 헬퍼: 프리미엄 리스트 아이템] ---
  const renderItem = (item) => {
    const isEditing = editingId === item.id;
    return (
      <div id={`checklist-${item.id}`} key={item.id} className={`checklist-item-container d-flex align-items-start px-3 py-2 border-bottom transition-all ${item.is_completed ? 'completed-item' : 'bg-white'}`}>

        {/* 둥근 체크박스 */}
        <div
          className={`me-3 mt-1 flex-shrink-0 ${isPureWatcher ? '' : 'cursor-pointer'}`}
          onClick={() => { if (!isPureWatcher) handleToggle(item.id, item.is_completed); }}
        >
          <div className={`premium-checkbox ${item.is_completed ? 'checked' : ''} ${isPureWatcher ? 'opacity-50' : ''}`} style={isPureWatcher ? { cursor: 'default' } : {}}>
            {item.is_completed && <i className="fas fa-check text-white" style={{ fontSize: '8px' }}></i>}
          </div>
        </div>

        {/* 내용 영역 (수정 모드 / 읽기 모드 분기) */}
        <div className="flex-grow-1 min-w-0 position-relative" style={{ paddingTop: '2px' }}>
          {isEditing ? (
            // [수정 모드] 하단 액션바 스타일 적용
            <div className="edit-panel bg-white rounded-3 border border-primary shadow-sm animation-fade-in d-flex flex-column overflow-hidden">
              <textarea
                ref={editAreaRef}
                className="form-control border-0 shadow-none p-2 custom-textarea custom-scrollbar bg-transparent flex-grow-1"
                style={{ fontSize: '12px', lineHeight: '1.5', resize: 'none', minHeight: '36px' }}
                value={editContent}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setEditContent(e.target.value);
                    handleTextareaResize(e);
                  }
                }}
                maxLength={500}
              />
              <div className="d-flex justify-content-between align-items-center bg-light px-2 py-1" style={{ borderTop: '1px solid #e9ecef' }}>
                <div className="text-muted fw-bold ps-1" style={{ fontSize: '0.6rem' }}>
                  {editContent.length}/500
                </div>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm bg-white border shadow-sm px-2" style={{ fontSize: '0.7rem', borderRadius: '4px', paddingTop: '2px', paddingBottom: '2px' }} onClick={() => setEditingId(null)}>취소</button>
                  <button className="btn btn-sm btn-primary shadow-sm fw-bold px-2" style={{ fontSize: '0.7rem', borderRadius: '4px', paddingTop: '2px', paddingBottom: '2px' }} onClick={saveEdit}>저장</button>
                </div>
              </div>
            </div>
          ) : (
            // [읽기 모드] 텍스트 표시 및 메타데이터 하단 좌측 배치
            <div className="d-flex flex-column w-100 position-relative content-wrapper">

              {/* 본문 텍스트 (Line Clamp 적용) */}
              <div
                className={`pe-5 ${item.is_completed ? 'text-decoration-line-through opacity-50' : 'text-dark'}`}
                style={{
                  fontSize: '12px', fontWeight: '400', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  display: expandedItems[item.id] || !isContentLong(item.content) ? 'block' : '-webkit-box',
                  WebkitLineClamp: expandedItems[item.id] || !isContentLong(item.content) ? 'unset' : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: expandedItems[item.id] || !isContentLong(item.content) ? 'visible' : 'hidden'
                }}
              >
                {item.content}
              </div>

              {/* 하단 제어부: 더보기/접기 (좌측) + 메타데이터 (우측) */}
              <div className="d-flex align-items-center mt-1 w-100">
                {isContentLong(item.content) && (
                  <span
                    className="text-primary fw-bold"
                    style={{ fontSize: '12px', cursor: 'pointer' }}
                    onClick={() => setExpandedItems(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                  >
                    {expandedItems[item.id] ? '- 접기' : '+ 더보기'}
                  </span>
                )}
                <div className="d-flex align-items-center gap-2 text-muted transition-opacity meta-info ms-auto" style={{ fontSize: '11px' }}>
                  {viewMode !== 'MINE' && item.user && (
                    <span className="fw-medium text-secondary">{item.user.name} {item.user.org?.position}</span>
                  )}
                  <span style={{ fontSize: '10px' }}><i className="far fa-clock me-1 opacity-50"></i>{formatDateTime(item.created_at)}</span>
                </div>
              </div>

              {/* 호버 액션 박스 (마우스 올릴 때만 표시) - 절대 위치로 상단 우측에 띄움 */}
              <div className="hover-actions d-flex gap-1 align-items-center bg-white rounded-2 shadow-sm px-1 py-1 border position-absolute top-0 end-0">
                <div style={{ position: 'relative' }}>
                  <button
                    className="btn btn-link p-1 text-secondary hover-info border-0 shadow-none"
                    onClick={(e) => handleOpenTodoRegister(e, item)}
                    title="To-Do 등록"
                  >
                    <i className="fas fa-tasks" style={{ fontSize: '0.75rem' }}></i>
                  </button>
                  {todoSource && todoSource.ref_id === item.id && (
                    <TodoRegisterLayer
                      source={todoSource}
                      anchorRef={todoAnchorEl}
                      onClose={() => setTodoSource(null)}
                      direction="down"
                      align="right"
                    />
                  )}
                </div>
                {(item.user?.id === currentUser?.id || isProjectMaster) && (
                  <>
                    <button className="btn btn-link p-1 text-secondary hover-primary border-0 shadow-none" onClick={(e) => { e.stopPropagation(); startEdit(item); }} title="수정">
                      <i className="fas fa-pencil-alt" style={{ fontSize: '0.75rem' }}></i>
                    </button>
                    <button className="btn btn-link p-1 text-secondary hover-danger border-0 shadow-none" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} title="삭제">
                      <i className="far fa-trash-alt" style={{ fontSize: '0.75rem' }}></i>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- [원형 진척도 차트 (SVG Ring)] ---
  const radius = 12;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (overallProgress / 100) * circumference;

  return (
    <div className="animation-fade-in d-flex flex-column h-100 p-1" style={{ fontFamily: "'Inter', 'Pretendard', sans-serif" }}>
      <style>{`
        /* Modern Underline Tabs */
        .modern-tabs {
          display: flex;
          gap: 20px;
          margin-bottom: -1px;
        }
        .modern-tab-btn {
          padding: 8px 4px 10px 4px;
          font-size: 14px;
          font-weight: 700;
          color: #6c757d;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }
        .modern-tab-btn.active {
          color: #4c6ef5;
        }
        .modern-tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background-color: #4c6ef5;
          border-radius: 3px 3px 0 0;
        }
        .modern-tab-btn:hover:not(.active) { color: #343a40; }

        /* Custom Premium Checkbox */
        .premium-checkbox {
          width: 15px; height: 15px;
          border-radius: 50%;
          border: 2px solid #ced4da;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s ease;
          background-color: white;
          cursor: pointer;
        }
        .premium-checkbox.checked {
          background-color: #4c6ef5; border-color: #4c6ef5;
          box-shadow: 0 2px 5px rgba(76,110,245,0.3);
        }
        .premium-checkbox:hover:not(.checked) { border-color: #4c6ef5; }
        .checklist-item-container:hover .premium-checkbox:not(.checked) { background-color: #f1f3f5; border-color: #adb5bd; }
        .cursor-pointer { cursor: pointer !important; }

        /* List Item Hover Effects */
        .checklist-item-container { transition: background-color 0.2s ease; }
        .checklist-item-container:hover { background-color: #fdfdfd; }
        .completed-item { background-color: #f7f7f7 !important; }

        /* Hover Actions Box */
        .hover-actions { opacity: 0; transform: translateX(5px); transition: all 0.2s ease; pointer-events: none; }
        .checklist-item-container:hover .hover-actions { opacity: 1; transform: translateX(0); pointer-events: auto; }
        .checklist-item-container:hover .meta-info { opacity: 0.1; } /* 마우스 올렸을 때 원래 메타정보 희미하게 */
        .hover-danger:hover { color: #fa5252 !important; }
        .hover-primary:hover { color: #4c6ef5 !important; }

        /* Custom Textarea */
        .custom-textarea:focus { outline: none; background-color: transparent; }

        /* Quick Add Sticky Footer */
        .sticky-quick-add {
          background-color: #f8f9fa;
        }
        .quick-add-input-wrapper {
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .quick-add-input-wrapper:focus-within {
          border-color: #4c6ef5 !important;
          box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.15) !important;
        }

        /* Custom Scrollbar */
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
      `}</style>

      {/* 메인 컨테이너 (테두리와 그림자가 있는 카드 형태 복원) */}
      <div className="bg-white rounded-3 border shadow-sm d-flex flex-column flex-grow-1" style={{ borderColor: '#dee2e6', overflow: 'hidden', minHeight: 0 }}>

        {/* 1. 최상단 통합 헤더 (여백 최적화) */}
        <div className="px-3 pt-2 border-bottom bg-light d-flex justify-content-between align-items-end flex-shrink-0 z-index-1">
          <div className="modern-tabs">
            <div className={`modern-tab-btn ${viewMode === 'MINE' ? 'active' : ''}`} onClick={() => handleTabChange('MINE')}>내 체크리스트</div>
            <div className={`modern-tab-btn ${viewMode === 'ALL' ? 'active' : ''}`} onClick={() => handleTabChange('ALL')}>전체 보기</div>
            <div className={`modern-tab-btn ${viewMode === 'GROUP' ? 'active' : ''}`} onClick={() => handleTabChange('GROUP')}>작성자별</div>
          </div>

          <div className="d-flex align-items-center gap-3 pb-2">
            {/* 완료 숨기기 토글 (왼쪽으로 이동) */}
            <div className="form-check form-switch mb-0 p-0 d-flex align-items-center" style={{ minHeight: 'auto' }}>
              <input className="form-check-input cursor-pointer shadow-sm m-0 float-none" type="checkbox" id="hideCompleteToggle" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} style={{ width: '30px', height: '16px' }} />
              <label className="form-check-label ms-2 text-muted cursor-pointer fw-bold mb-0" htmlFor="hideCompleteToggle" style={{ fontSize: '0.7rem', lineHeight: '16px' }}>완료 숨기기</label>
            </div>

            {/* 통합 통계 위젯 (총 갯수 + 진척도) */}
            <div className="d-flex align-items-center gap-2 bg-white border rounded-pill px-3 py-1 shadow-sm">
              <span className="fw-bold text-muted" style={{ fontSize: '12px' }}>
                총 <span className="text-dark fw-bolder">{baseItems.length}</span>건
              </span>

              <div className="border-start border-2 mx-1" style={{ height: '14px' }}></div>

              <div className="d-flex align-items-center gap-1" title={`진척도: ${overallProgress}%`}>
                <svg width="24" height="24" viewBox="0 0 28 28">
                  <circle cx="14" cy="14" r={radius} fill="none" stroke="#e9ecef" strokeWidth="3" />
                  <circle cx="14" cy="14" r={radius} fill="none" stroke="#4c6ef5" strokeWidth="3"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.5s ease', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                  />
                </svg>
                <span className="fw-bolder text-dark" style={{ fontSize: '0.75rem' }}>{overallProgress}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2. 리스트 메인 영역 */}
        <div className="flex-grow-1 overflow-auto custom-scrollbar bg-white">
          {checklists.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
              <i className="fas fa-clipboard-check fs-1 mb-3 opacity-25"></i>
              <h6 className="fw-bolder fs-7">등록된 체크리스트가 없습니다.</h6>
            </div>
          ) : (
            <div>
              {viewMode === 'GROUP' ? (
                // [작성자별 그룹 뷰]
                Object.values(groupedData).sort((a, b) => {
                  if (a.user.id === currentUser?.id) return -1;
                  if (b.user.id === currentUser?.id) return 1;
                  return (a.user.name || '').localeCompare(b.user.name || '');
                }).map((group, index) => {
                  const isExpanded = expandedGroups[group.user.id];
                  const groupTotal = group.items.length;
                  const groupCompleted = group.items.filter(i => i.is_completed).length;

                  return (
                    <div key={group.user.id}>
                      <div
                        className="d-flex align-items-center justify-content-between px-3 py-2 bg-light transition-all"
                        style={{ borderTop: index === 0 ? '1px solid #ced4da' : '1px solid #e9ecef', borderBottom: '1px solid #dee2e6', cursor: 'pointer' }}
                        onClick={() => toggleGroup(group.user.id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex align-items-center justify-content-center cursor-pointer" style={{ width: '20px', height: '20px' }}>
                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-secondary`} style={{ fontSize: '13px' }}></i>
                          </div>
                          <span className="fw-bolder text-dark" style={{ fontSize: '13px' }}>{group.user.name} {group.user.org.position}</span>
                        </div>
                        <span className="fw-bold text-muted bg-white border px-2 py-0 rounded-pill" style={{ fontSize: '12px' }}>{groupCompleted} / {groupTotal}</span>
                      </div>

                      {isExpanded && (
                        <div className="bg-white">
                          {group.items.map(renderItem)}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // [전체/내할일 플랫 뷰]
                <div>
                  {filteredData.map(renderItem)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. 고정된 하단 퀵 추가 입력창 (개편된 UI) */}
        {(viewMode === 'ALL' || viewMode === 'MINE') && !isPureWatcher && (
          <div className="sticky-quick-add px-3 py-3 border-top flex-shrink-0">
            <div className="quick-add-input-wrapper bg-white border border-light-subtle rounded-3 shadow-sm d-flex flex-column overflow-hidden">
              <div className="d-flex align-items-start p-2">
                <i className="fas fa-plus text-primary opacity-50 me-2 mt-1 fs-6 flex-shrink-0"></i>
                <textarea
                  ref={quickAddRef}
                  className="form-control border-0 shadow-none p-0 custom-scrollbar bg-transparent flex-grow-1"
                  style={{ fontSize: '12px', resize: 'none', minHeight: '36px', maxHeight: '120px', lineHeight: '1.5' }}
                  placeholder="새로운 체크리스트를 입력하세요."
                  value={newChecklist || ''}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setNewChecklist(e.target.value);
                      handleTextareaResize(e);
                    }
                  }}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center bg-light px-2 py-1" style={{ borderTop: '1px solid #e9ecef' }}>
                <div className="text-muted fw-bold ps-1" style={{ fontSize: '0.6rem' }}>
                  {(newChecklist || '').length}/500
                </div>
                <button
                  className="btn btn-sm btn-primary px-3 shadow-sm fw-bold d-flex align-items-center gap-1"
                  style={{ fontSize: '0.7rem', borderRadius: '4px', paddingTop: '2px', paddingBottom: '2px' }}
                  onClick={handleQuickAddSave}
                  disabled={!newChecklist || newChecklist.trim() === ''}
                >
                  <i className="fas fa-paper-plane"></i> 등록
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default ChecklistTab;
